import {
  CacheData,
  Dictionary,
  InstantConfigCacheStorageService,
} from "@wikia/instant-config-loader";
import {
  Action,
  CommunicationService,
  eventsRepository,
} from "@identity/services";
import { deserializeCache, serializeCache } from "./cache-serializer";
import { Logger, TopLevelDomainExtractor } from "@identity/utils";
import { container } from "tsyringe";

export class InstantConfigCacheService
  implements InstantConfigCacheStorageService
{
  private cacheStorage: Dictionary<CacheData> = {};
  private readonly cacheKey = "basset";
  private readonly logger: Logger;

  constructor(
    private readonly window: Window,
    private readonly cookiesManager: CookiesManager,
    private readonly communicationService: CommunicationService,
  ) {
    const logger = container.resolve(Logger);
    logger.setLoggerGroup("InstantConfig");
    this.logger = logger;
    this.resetCache();
  }

  resetCache(): void {
    const serializedCache = this.cookiesManager.get(this.cacheKey) || "";
    this.cacheStorage = deserializeCache(serializedCache);
    this.logger.info("reset cache", this.cacheStorage);
  }

  get(id: string): CacheData {
    return this.cacheStorage[id];
  }

  set(data: CacheData): void {
    this.cacheStorage[data.name] = data;
  }

  remove(id: string) {
    delete this.cacheStorage[id];
  }

  synchronizeCookie(): void {
    this.communicationService.on(
      eventsRepository.CONSENT_READY,
      ({ gdprConsent }) => {
        if (gdprConsent) {
          this.saveCookie();
          this.communicationService.emit(
            new Action(eventsRepository.INSTANT_CONFIG_CACHE_READY),
          );
        }
      },
    );
  }

  private saveCookie(): void {
    const cacheDictionaryWithCookie: Dictionary<CacheData> = Object.keys(
      this.cacheStorage,
    )
      .map((key) => ({
        key,
        value: this.cacheStorage[key],
      }))
      .filter(({ value }) => value.withCookie)
      .reduce((result, { key, value }) => ({ ...result, [key]: value }), {});

    const domain = this.window.location.hostname;
    const tld = TopLevelDomainExtractor.extract(domain);
    const cacheToSave = serializeCache(cacheDictionaryWithCookie);
    this.cookiesManager.set(this.cacheKey, cacheToSave, {
      domain: tld,
      expires: 180,
    });
    this.logger.info("saved in cookie", this.cacheStorage, cacheToSave);
  }
}
